<script lang="ts" setup>
import { faSparkles, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { faEdit, faTrash } from "@fortawesome/pro-solid-svg-icons";
import type { FieldDefinition } from "~/models/Automation";
import { makeVariable } from "~/utils";

const editable = defineModel<FieldDefinition>("editable", {
    required: false,
    default: undefined,
});

const { item } = defineProps<{
    search?: string;
    noCopy?: boolean;
    item: FieldDefinition;
    fields: FieldDefinition[];
}>();

defineEmits<{
    (e: "update:deletable", v: FieldDefinition): void;
    (e: "saved", v: { key: string; close: () => void }): void;
}>();

const isShowingItems = ref<boolean>();

const displayValue = computed(() => {
    if (item.type === "list") {
        return (
            item.options?.find((option) => option.value === item.default_value)
                ?.label ?? item.default_value
        );
    }

    let value = item.default_value;

    if (typeof value === 'string' && value.startsWith('<') && value.endsWith('>')) {
        return value.slice(1, -1);
    }

    return value;
});
</script>

<template>
    <UIDataListItem condensed>
        <div
            :style="
                item?.sub_type === 'color'
                    ? {
                          borderColor: displayValue
                      }
                    : undefined
            "
            class="flex gap-2 border-l-4 -ml-1.5 pl-1.5 border-transparent"
            :class="noCopy ? 'items-center' : 'items-start'"
        >
            <UIAvatar
                v-if="item.avatar"
                :alt="item.avatar.alt"
                :src="item.avatar.src"
                size="none"
                class="transform h-5 w-5 translate-y-0.5"
            />
            <div class="flex flex-col gap-1 flex-1 truncate">
                <UPopover
                    v-if="
                        item.metadata?.is_template_field ||
                        item.metadata?.is_editable
                    "
                    :popper="{ placement: 'left-end' }"
                >
                    <UButton
                        :padded="false"
                        class="group font-medium text-sm truncate hover:underline"
                        color="white"
                        variant="ghost"
                        @click="editable = item"
                    >
                        <FontAwesomeIcon :icon="faSparkles" />
                        <Mark
                            :keyword="search"
                            :text="
                                (item.label ?? item.key).replaceAll(' : ', ': ')
                            "
                        />
                        <FontAwesomeIcon
                            :icon="faEdit"
                            class="opacity-0 group-hover:opacity-50 ml-2"
                        />
                    </UButton>
                    <template #panel="{ close }">
                        <div class="relative p-4 max-h-[70vh]">
                            <h3 class="font-display text-lg mb-4">
                                Edit variable
                                <UButton
                                    class="absolute right-4 top-4"
                                    color="white"
                                    square
                                    @click="$emit('update:deletable', item)"
                                >
                                    <FontAwesomeIcon :icon="faTrash" />
                                </UButton>
                            </h3>
                            <CreateFieldDefinition
                                :key="editable?.key ?? 'field'"
                                :fields="fields"
                                v-model:field="editable"
                                @cancelled="close()"
                                @saved="
                                    $emit('saved', {
                                        key: item.key,
                                        close,
                                    })
                                "
                            />
                        </div>
                    </template>
                </UPopover>
                <UButton
                    v-else-if="item.options?.length"
                    class="flex items-center hover:text-primary-500 active:scale-100"
                    variant="link"
                    color="white"
                    :padded="false"
                    @click="isShowingItems = !isShowingItems"
                >
                    <template #trailing>
                        <FontAwesomeIcon
                            :icon="faChevronRight"
                            class="transform transition-transform duration-150"
                            size="xs"
                            :class="[isShowingItems ? 'rotate-90' : '']"
                        />
                    </template>
                    <Mark
                        class="font-medium text-sm truncate pt-2.5 -mt-2"
                        :keyword="search"
                        :text="
                            (item.label ?? item.key)
                                .replaceAll(' : ', ': ')
                                .trimEnd()
                        "
                    />
                </UButton>
                <Mark
                    v-else
                    class="font-medium text-sm truncate pt-2.5 -mt-2"
                    :keyword="search"
                    :text="(item.label ?? item.key).replaceAll(' : ', ': ')"
                />
                <div v-if="item.description" class="text-xs mb-0.5 whitespace-pre-wrap text-gray-500">
                    {{ item.description }}
                </div>
                <UICopyButton
                    v-if="!noCopy"
                    :keyword="search"
                    :source="makeVariable(item?.key)"
                />
            </div>
            <UBadge
                :label="item?.sub_type ?? item?.type ?? ''"
                class="rounded-full"
                color="gray"
            />
        </div>
        <div class="pl-8">
            <div v-if="isShowingItems" class="prose prose-sm">
                <ul>
                    <li v-for="option in item.options" :key="option.value">
                        {{ option.label }}
                    </li>
                </ul>
            </div>
            <div
                v-if="item?.sub_type === 'image' && displayValue"
                class="mt-2 relative p-3 bg-hd-neutral-50 rounded-xl block"
            >
                <nuxt-img :src="displayValue" class="rounded-lg max-h-32" />
            </div>
            <div
                v-else-if="
                    item?.type === 'bool' && typeof displayValue === 'boolean'
                "
                class="mt-2 text-xs px-3 py-2 bg-hd-neutral-50 rounded-xl block truncate underline"
            >
                {{ displayValue ? "Yes" : "No" }}
            </div>
            <nuxt-link
                v-else-if="
                    item.type === 'string' &&
                    displayValue &&
                    item.sub_type === 'url'
                "
                :to="displayValue"
                class="mt-2 text-xs px-3 py-2 bg-hd-neutral-50 rounded-xl block truncate underline"
                external
                target="_blank"
            >
                {{ displayValue }}
            </nuxt-link>
            <nuxt-link
                v-else-if="
                    item.type === 'string' &&
                    displayValue &&
                    item.sub_type === 'domain'
                "
                :to="`https://${displayValue}`"
                class="mt-2 text-xs px-3 py-2 bg-hd-neutral-50 rounded-xl block truncate underline"
                external
                target="_blank"
            >
                {{ displayValue }}
            </nuxt-link>
            <nuxt-link
                v-else-if="
                    item.type === 'string' &&
                    displayValue &&
                    item.sub_type === 'email'
                "
                :to="`mailto:${displayValue}`"
                class="mt-2 text-xs px-3 py-2 bg-hd-neutral-50 rounded-xl block truncate underline"
                external
            >
                {{ displayValue }}
            </nuxt-link>
            <ClientOnly
                v-else-if="
                    ['string', 'datetime', 'date', 'time', 'list', 'number', 'int', 'float'].includes(
                        item.type,
                    ) &&
                    displayValue &&
                    !['color'].includes(item.sub_type)
                "
            >
                <div
                    class="mt-2 bg-hd-neutral-50 px-3 py-2 overflow-hidden rounded-xl"
                >

                    <UiDataItem
                        :item-class="['prose-sm !text-xs']"
                        :model-value="
                            ['string', 'list', 'number', 'int', 'float'].includes(item.type)
                                ? displayValue
                                : $dayjs(displayValue)
                        "
                    />
                </div>
            </ClientOnly>
        </div>
    </UIDataListItem>
</template>
